import { addDeviceGroup, deleteDeviceGroup, DeviceGroupType, getDeviceGroup, sortDeviceGroup, updateDeviceGroup } from '@/api';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Card, Input, message, Popconfirm, Space, Spin, Tree } from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import React, { useEffect, useRef, useState } from 'react';

const { useMessage } = message;

export interface TreeNode extends DataNode {
  title: string;
  key: string;
  children?: TreeNode[];
  parent_id?: string;
  sort?: number;
}

export interface GroupTreeProps {
  selectedKeys: React.Key[];
  onSelect: (keys: React.Key[]) => void;
  onRefresh?: () => void;
}

const GroupTree: React.FC<GroupTreeProps> = ({ selectedKeys, onSelect, onRefresh }) => {
  const [messageApi, contextHolder] = useMessage();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const inputRef = useRef<any>(null);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // 将接口数据转换为树形结构
  const convertToTreeData = (data: DeviceGroupType[]): TreeNode[] => {
    return data.map((item) => ({
      title: item.name,
      key: item.id,
      parent_id: item.parent_id,
      sort: item.sort,
      children: item.children ? convertToTreeData(item.children) : undefined,
    }));
  };

  // 获取分组数据
  const loadTreeData = async () => {
    try {
      setTreeLoading(true);
      const response = await getDeviceGroup();
      if (response.success && response.result) {
        const treeNodes = convertToTreeData(response.result);
        setTreeData(treeNodes);

        // 如果有根节点，默认展开并选中第一个根节点（仅首次加载时）
        if (treeNodes.length > 0) {
          const rootNodeKey = treeNodes[0].key;

          // 首次加载时，只展开根节点并选中根节点
          if (isFirstLoad && selectedKeys.length === 0) {
            setExpandedKeys([rootNodeKey]);
            onSelect([rootNodeKey]);
            setIsFirstLoad(false);
          }
        }
      } else {
        messageApi.error(response.errorMsg || '获取分组数据失败');
      }
    } catch (error) {
      messageApi.error('获取分组数据失败');
    } finally {
      setTreeLoading(false);
    }
  };

  useEffect(() => {
    loadTreeData();
  }, []);

  // 添加节点
  const handleAdd = (node: TreeNode) => {
    const loop = (data: TreeNode[], key: React.Key, callback: (node: TreeNode, index: number, data: TreeNode[]) => void) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) {
          return callback(data[i], i, data);
        }
        if (data[i].children) {
          loop(data[i].children!, key, callback);
        }
      }
    };

    const data = [...treeData];
    const newKey = `new-node-${Date.now()}`;
    const newNode: TreeNode = {
      title: '',
      key: newKey,
    };

    loop(data, node.key, (item) => {
      if (!item.children) {
        item.children = [];
      }
      item.children.unshift(newNode);
    });

    setTreeData(data);
    setEditingKey(newKey);
    setEditingValue('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // 编辑节点
  const handleEdit = (node: TreeNode) => {
    setEditingKey(node.key);
    setEditingValue(node.title);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // 保存编辑
  const handleSave = async (key: string) => {
    if (!editingValue.trim()) {
      message.warning('节点名称不能为空');
      return;
    }

    const isNewNode = key.startsWith('new-node-');

    try {
      if (isNewNode) {
        // 新建节点
        const loop = (data: TreeNode[], key: React.Key, callback: (node: TreeNode, parent?: TreeNode) => void, parent?: TreeNode) => {
          for (let i = 0; i < data.length; i++) {
            if (data[i].key === key) {
              return callback(data[i], parent);
            }
            if (data[i].children) {
              loop(data[i].children!, key, callback, data[i]);
            }
          }
        };

        let parentId: string | undefined;
        loop(treeData, key, (node, parent) => {
          parentId = parent?.key as string | undefined;
        });

        const response = await addDeviceGroup({
          name: editingValue.trim(),
          parent_id: parentId,
          sort: 0,
        });

        if (response.success) {
          messageApi.success('添加成功');
          await loadTreeData();
          onRefresh?.();
        } else {
          messageApi.error(response.errorMsg || '添加失败');
        }
      } else {
        // 更新节点
        const findNodeAndParent = (data: TreeNode[], targetKey: React.Key, parent?: TreeNode): { node: TreeNode | null; parent: TreeNode | null; index: number } => {
          for (let j = 0; j < data.length; j++) {
            if (data[j].key === targetKey) {
              return { node: data[j], parent: parent || null, index: j };
            }
            if (data[j].children) {
              const found = findNodeAndParent(data[j].children!, targetKey, data[j]);
              if (found.node) return found;
            }
          }
          return { node: null, parent: null, index: -1 };
        };

        const { node: nodeData, parent: parentNode } = findNodeAndParent(treeData, key);

        const response = await updateDeviceGroup({
          id: key,
          name: editingValue.trim(),
          parent_id: parentNode?.key as string | undefined,
          sort: nodeData?.sort ?? 0,
        });

        if (response.success) {
          messageApi.success('更新成功');
          await loadTreeData();
          onRefresh?.();
        } else {
          messageApi.error(response.errorMsg || '更新失败');
        }
      }
    } catch (error) {
      messageApi.error(isNewNode ? '添加失败' : '更新失败');
    } finally {
      setEditingKey(null);
      setEditingValue('');
    }
  };

  // 取消编辑
  const handleCancel = (key: string) => {
    if (key.startsWith('new-node-')) {
      const loop = (data: TreeNode[], key: React.Key, callback: (node: TreeNode, index: number, data: TreeNode[]) => void) => {
        for (let i = 0; i < data.length; i++) {
          if (data[i].key === key) {
            return callback(data[i], i, data);
          }
          if (data[i].children) {
            loop(data[i].children!, key, callback);
          }
        }
      };

      const data = [...treeData];
      loop(data, key, (item, index, arr) => {
        arr.splice(index, 1);
      });
      setTreeData(data);
    }

    setEditingKey(null);
    setEditingValue('');
  };

  // 删除节点
  const handleDelete = async (node: TreeNode) => {
    try {
      const response = await deleteDeviceGroup(node.key as string);
      if (response.success) {
        messageApi.success('删除成功');
        await loadTreeData();
        onRefresh?.();
      } else {
        messageApi.error(response.errorMsg || '删除失败');
      }
    } catch (error) {
      messageApi.error('删除失败');
    }
  };

  // 自定义节点标题渲染
  const titleRender = (nodeData: TreeNode) => {
    const isEditing = editingKey === nodeData.key;

    if (isEditing) {
      return (
        <div
          className="tree-node-title"
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Input
            ref={inputRef}
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onPressEnter={() => handleSave(nodeData.key)}
            onBlur={() => handleSave(nodeData.key)}
            style={{ flex: 1, minWidth: 0 }}
            size="small"
          />
          <Space
            size="small"
            style={{
              marginLeft: 8,
              flexShrink: 0,
            }}
          >
            <CheckOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }} onClick={() => handleSave(nodeData.key)} />
            <CloseOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }} onClick={() => handleCancel(nodeData.key)} />
          </Space>
        </div>
      );
    }

    return (
      <div
        className="tree-node-title"
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        <span
          style={{
            flex: 1,
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {nodeData.title || '未命名'}
        </span>
        <Space
          size="small"
          onClick={(e) => e.stopPropagation()}
          className="tree-node-actions"
          style={{
            marginLeft: 8,
            opacity: 0,
            transition: 'opacity 0.2s',
            flexShrink: 0,
          }}
        >
          <PlusOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }} onClick={() => handleAdd(nodeData)} />
          <EditOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }} onClick={() => handleEdit(nodeData)} />
          <Popconfirm title={`此操作将永久删除分组"${nodeData.title}", 要继续吗？`} okText="确定" cancelText="取消" onConfirm={() => handleDelete(nodeData)}>
            <DeleteOutlined style={{ color: '#172c4d', cursor: 'pointer', fontSize: 12 }} />
          </Popconfirm>
        </Space>
      </div>
    );
  };

  // 树拖拽处理
  const onDrop: TreeProps['onDrop'] = async (info) => {
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const loop = (data: TreeNode[], key: React.Key, callback: (node: TreeNode, i: number, data: TreeNode[]) => void) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) {
          return callback(data[i], i, data);
        }
        if (data[i].children) {
          loop(data[i].children!, key, callback);
        }
      }
    };

    const data = [...treeData];

    let dragObj: TreeNode | undefined;
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!dragObj) {
      return;
    }

    let newParentId: string | undefined;
    let newSort = 0;

    if (!info.dropToGap) {
      loop(data, dropKey, (item) => {
        item.children = item.children || [];
        item.children.unshift(dragObj!);
        newParentId = item.key as string;
        newSort = 0;
      });
    } else if ((info.node.children || []).length > 0 && info.node.expanded && dropPosition === 1) {
      loop(data, dropKey, (item) => {
        item.children = item.children || [];
        item.children.unshift(dragObj!);
        newParentId = item.key as string;
        newSort = 0;
      });
    } else {
      let ar: TreeNode[] = [];
      let i: number;
      let parentNode: TreeNode | null = null;

      const findParent = (data: TreeNode[], targetKey: React.Key, parent?: TreeNode): TreeNode | null => {
        for (let j = 0; j < data.length; j++) {
          if (data[j].key === targetKey) {
            return parent || null;
          }
          if (data[j].children) {
            const found = findParent(data[j].children!, targetKey, data[j]);
            if (found !== null) return found;
          }
        }
        return null;
      };

      parentNode = findParent(data, dropKey);
      newParentId = parentNode?.key as string | undefined;

      loop(data, dropKey, (item, index, arr) => {
        ar = arr;
        i = index;
      });

      if (dropPosition === -1) {
        ar.splice(i!, 0, dragObj!);
        newSort = i!;
      } else {
        ar.splice(i! + 1, 0, dragObj!);
        newSort = i! + 1;
      }
    }

    const findNodeInTree = (data: TreeNode[], targetKey: React.Key, parent?: TreeNode): { node: TreeNode | null; parent: TreeNode | null; index: number } => {
      for (let j = 0; j < data.length; j++) {
        if (data[j].key === targetKey) {
          return { node: data[j], parent: parent || null, index: j };
        }
        if (data[j].children) {
          const found = findNodeInTree(data[j].children!, targetKey, data[j]);
          if (found.node) return found;
        }
      }
      return { node: null, parent: null, index: -1 };
    };

    const { node: finalNode, parent: finalParent, index: finalIndex } = findNodeInTree(data, dragKey);
    if (finalNode && finalIndex >= 0) {
      newSort = finalIndex;
      newParentId = finalParent?.key as string | undefined;
    }

    setTreeData(data);

    try {
      const response = await sortDeviceGroup({
        id: dragKey as string,
        sort: newSort,
        parent_id: newParentId,
      });

      if (response.success) {
        await loadTreeData();
        onRefresh?.();
      } else {
        messageApi.error(response.errorMsg || '更新排序失败');
        await loadTreeData();
      }
    } catch (error) {
      messageApi.error('更新排序失败');
      await loadTreeData();
    }
  };

  return (
    <>
      {contextHolder}
      <Card size="small" title="设备分组" style={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
        <style>
          {`
            .tree-node-title:hover .tree-node-actions {
              opacity: 1 !important;
            }
            .tree-container {
              flex: 1;
              overflow-y: auto;
              overflow-x: auto;
              min-height: 0;
            }
            .tree-container .ant-tree-node-selected .ant-tree-node-content-wrapper,
            .tree-container .ant-tree-node-content-wrapper.ant-tree-node-selected {
              background-color: rgba(24, 144, 255, 0.04) !important;
            }
            .tree-container .ant-tree-node-selected .ant-tree-node-content-wrapper:hover,
            .tree-container .ant-tree-node-content-wrapper.ant-tree-node-selected:hover {
              background-color: rgba(24, 144, 255, 0.06) !important;
            }
            .tree-container .ant-tree-node-selected .tree-node-actions {
              opacity: 1 !important;
            }
          `}
        </style>
        <div className="tree-container">
          <Spin spinning={treeLoading}>
            <Tree
              treeData={treeData}
              draggable
              blockNode
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              onDrop={onDrop}
              titleRender={titleRender}
              selectedKeys={selectedKeys}
              onSelect={(keys) => onSelect(keys)}
            />
          </Spin>
        </div>
      </Card>
    </>
  );
};

export default GroupTree;
